import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Calendar, Folder as FolderIcon, Tag, User } from "lucide-react";
import {
  ASSET_TYPE_LABEL,
  ASSET_VISIBILITIES,
  ASSET_VISIBILITY_LABEL,
  EMBED_PROVIDER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  type AssetType,
  type AssetVisibility,
  type PartnerType,
} from "@colab/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { formatRelative } from "@/lib/utils";
import { AssetPreview } from "./asset-preview";
import type { ContentAssetRow } from "./types";
import { useAssetDetail } from "./use-asset-detail";
import { useUpdateAsset } from "./use-update-asset";

// Phase 7 sub-bucket 13. Asset detail Metadata tab. Two columns at lg+:
// preview pane (iframe / link card / file card / folder children) on the
// left, autosave form sidebar on the right. Each field wraps useUpdateAsset
// in useAutosave (750ms); status bubbles up to the page header via the
// onSaveStatusChange callback so SaveIndicator can render the aggregate.
//
// `parentRow` is loaded via useAssetDetail of the parent's id — small extra
// fetch, but keeps the breadcrumb correct without coupling the route to
// the parent's title.
export function AssetMetadataTab({
  asset,
  onSaveStatusChange,
}: {
  asset: ContentAssetRow;
  onSaveStatusChange: (next: SaveStatus) => void;
}) {
  const parent = useAssetDetail(asset.parent_asset_id ?? undefined);
  const update = useUpdateAsset();

  // ---- Per-field local state seeded from the cached asset ----
  const [description, setDescription] = useState(asset.description ?? "");
  const [assetType, setAssetType] = useState<AssetType>(asset.asset_type);
  const [visibility, setVisibility] = useState<AssetVisibility>(asset.visibility);
  const [partnerTypes, setPartnerTypes] = useState<Set<PartnerType>>(
    new Set(asset.partner_type_restriction),
  );
  const [categoriesRaw, setCategoriesRaw] = useState(asset.categories.join(", "));

  // Reseed when navigating between assets — different `asset.id` means a
  // wholly different row; we don't want stale field state across detail
  // navigations.
  useEffect(() => {
    setDescription(asset.description ?? "");
    setAssetType(asset.asset_type);
    setVisibility(asset.visibility);
    setPartnerTypes(new Set(asset.partner_type_restriction));
    setCategoriesRaw(asset.categories.join(", "));
  }, [asset.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Per-field autosaves. One useAutosave per field gives independent
  // SaveStatus, which composes via aggregateSaveStatus in the page header.
  // Each flush re-reads no cache (the field's local state IS the source);
  // simpler than the tier-matrix mutator pattern because content fields
  // don't share a cache key with optimistic writes.
  const descSave = useAutosave<string>({
    onFlush: async (next) => {
      await update.mutateAsync({
        id: asset.id,
        patch: { description: next.trim() || null },
      });
    },
    wait: 750,
  });
  const visibilitySave = useAutosave<AssetVisibility>({
    onFlush: async (next) => {
      await update.mutateAsync({ id: asset.id, patch: { visibility: next } });
    },
    wait: 250, // chip-style change, faster than typed fields
  });
  const assetTypeSave = useAutosave<AssetType>({
    onFlush: async (next) => {
      await update.mutateAsync({ id: asset.id, patch: { asset_type: next } });
    },
    wait: 250,
  });
  const partnerTypeSave = useAutosave<PartnerType[]>({
    onFlush: async (next) => {
      await update.mutateAsync({
        id: asset.id,
        patch: { partner_type_restriction: next },
      });
    },
    wait: 400,
  });
  const categoriesSave = useAutosave<string[]>({
    onFlush: async (next) => {
      await update.mutateAsync({ id: asset.id, patch: { categories: next } });
    },
    wait: 750,
  });

  // Bubble the worst status up to the page header.
  useEffect(() => {
    const order: SaveStatus[] = ["error", "saving", "saved", "idle"];
    const statuses = [
      descSave.status,
      visibilitySave.status,
      assetTypeSave.status,
      partnerTypeSave.status,
      categoriesSave.status,
    ];
    const next =
      order.find((s) => statuses.includes(s)) ?? ("idle" as SaveStatus);
    onSaveStatusChange(next);
  }, [
    descSave.status,
    visibilitySave.status,
    assetTypeSave.status,
    partnerTypeSave.status,
    categoriesSave.status,
    onSaveStatusChange,
  ]);

  function togglePartnerType(t: PartnerType) {
    setPartnerTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      partnerTypeSave.queue(Array.from(next));
      return next;
    });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        <AssetPreview asset={asset} />
      </div>

      <aside className="space-y-4 rounded-xl border bg-card p-5 text-sm">
        {!asset.is_folder && (
          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-xs">
              Description
            </Label>
            <Textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                descSave.queue(e.target.value);
              }}
              onBlur={() => descSave.flush()}
            />
          </div>
        )}

        {!asset.is_folder && (
          <div className="space-y-1.5">
            <Label htmlFor="asset_type" className="text-xs">
              Type
            </Label>
            <select
              id="asset_type"
              value={assetType}
              onChange={(e) => {
                const v = e.target.value as AssetType;
                setAssetType(v);
                assetTypeSave.queue(v);
              }}
              className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
            >
              {(Object.keys(ASSET_TYPE_LABEL) as AssetType[]).map((t) => (
                <option key={t} value={t}>
                  {ASSET_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="visibility" className="text-xs">
            Visibility
          </Label>
          <select
            id="visibility"
            value={visibility}
            onChange={(e) => {
              const v = e.target.value as AssetVisibility;
              setVisibility(v);
              visibilitySave.queue(v);
            }}
            className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
          >
            {ASSET_VISIBILITIES.map((v) => (
              <option key={v} value={v}>
                {ASSET_VISIBILITY_LABEL[v]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs">Restrict to partner types</Label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {PARTNER_TYPES.map((t) => (
              <label
                key={t}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted has-[:checked]:border-accent has-[:checked]:bg-accent/10 has-[:checked]:text-accent"
              >
                <input
                  type="checkbox"
                  checked={partnerTypes.has(t)}
                  onChange={() => togglePartnerType(t)}
                  className="h-3 w-3 accent-accent"
                />
                {PARTNER_TYPE_LABEL[t]}
              </label>
            ))}
          </div>
        </div>

        {!asset.is_folder && (
          <div className="space-y-1.5">
            <Label htmlFor="categories" className="text-xs">
              Categories
            </Label>
            <Input
              id="categories"
              value={categoriesRaw}
              onChange={(e) => {
                setCategoriesRaw(e.target.value);
                const parsed = e.target.value
                  .split(",")
                  .map((c) => c.trim())
                  .filter((c) => c.length > 0);
                categoriesSave.queue(parsed);
              }}
              onBlur={() => categoriesSave.flush()}
              placeholder="meeting link, slides, banner"
            />
          </div>
        )}

        <div className="space-y-3 border-t pt-3 text-xs text-muted-foreground">
          {asset.embed_provider && (
            <DetailRow
              icon={Tag}
              label="Provider"
              value={EMBED_PROVIDER_LABEL[asset.embed_provider]}
            />
          )}
          {parent.data?.asset && (
            <DetailRow
              icon={FolderIcon}
              label="Folder"
              value={
                <Link
                  to={`/admin/content?folder=${parent.data.asset.id}`}
                  className="text-accent hover:underline"
                >
                  {parent.data.asset.title}
                </Link>
              }
            />
          )}
          <DetailRow
            icon={User}
            label="Created by"
            value={asset.uploaded_by ?? "—"}
          />
          <DetailRow
            icon={Calendar}
            label="Last updated"
            value={formatRelative(asset.updated_at)}
          />
          {asset.archived_at && (
            <DetailRow
              icon={Calendar}
              label="Archived"
              value={formatRelative(asset.archived_at)}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof FolderIcon;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="mt-0.5 text-foreground">{value}</div>
      </div>
    </div>
  );
}
